import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from "@angular/core"
import { Observable, Subscription } from "rxjs"
import { NgClass } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { MatCheckboxModule } from "@angular/material/checkbox"
import { KngService } from "src/app/services"
import { PropertyRoom } from "src/app/models"

type Room = {
   name: string
   count: number
   selected?: boolean
}

@Component({
   selector: "valben-rooms-input",
   templateUrl: "./rooms-input.component.html",
   standalone: true,
   imports: [
      NgClass,
      FormsModule,
      MatCheckboxModule,
   ],
})
export class RoomsInputComponent implements OnInit, OnDestroy {
   @Input() key: string = ""
   @Input() itemObservable!: () => Observable<any> | undefined
   @Input() update!: (value: any) => void
   @Input() title: string = ""
   @Input() error: boolean = false
   @Input() preview: boolean = false

   rooms: Room[] = []
   roomOptions: string[] = []
   selectedRooms: Room[] = []

   private _subscription: Subscription = new Subscription()
   private _lastValue: any = undefined

   constructor(private _kngService: KngService, private _cdr: ChangeDetectorRef) { }

   ngOnInit(): void {
      // The options list is fetched unconditionally: chaining it behind the item
      // observable left the list empty when the dialog opened.
      this._subscription.add(this._kngService.GetRooms().subscribe({
         next: rooms => {
            this.roomOptions = rooms
            this.applyValue(this._lastValue)
         },
         error: error => {
            console.error("Greška kod dohvaćanja soba:", error)
            this.roomOptions = (this._lastValue?.[this.key] ?? []).map((x: PropertyRoom) => x.name)
            this.applyValue(this._lastValue)
         },
      }))

      this._subscription.add(this.itemObservable()?.subscribe(value => {
         this._lastValue = value
         this.applyValue(value)
      }))
   }

   ngOnDestroy(): void {
      this._subscription.unsubscribe()
   }

   private applyValue(value: any): void {
      const existingRooms: PropertyRoom[] = value?.[this.key] ?? []

      this.rooms = this.roomOptions.map(roomName => ({
         name: roomName,
         count: existingRooms.find((x: PropertyRoom) => x.name === roomName)?.count ?? 1,
      }))

      this.selectedRooms = this.rooms.filter((room: Room) =>
         existingRooms.some((existing: PropertyRoom) => existing.name === room.name)
      )
      this.syncSelected()
      this._cdr.markForCheck()
   }

   clampCount(room: Room): void {
      room.count = Math.min(10, Math.max(0, room.count || 0))
      this.onChange()
   }

   toggleRoom(room: Room, checked: boolean): void {
      this.selectedRooms = checked
         ? [...this.selectedRooms, room]
         : this.selectedRooms.filter(x => x.name !== room.name)
      this.onChange()
   }

   onChange(): void {
      this.syncSelected()

      const selected = this.rooms
         .filter(room => this.selectedRooms.some(selectedRoom => selectedRoom.name === room.name))
         .map(room => ({
            name: room.name,
            count: room.count,
         }))

      this.update(selected)
   }

   private syncSelected(): void {
      this.rooms.forEach(room => room.selected = this.selectedRooms.some(x => x.name === room.name))
   }
}
